import React, {forwardRef, useCallback, useContext, useEffect, useRef, useState} from "react";
import {Box, CheckBox, ResponsiveContext, Skeleton, Text, ThemeContext} from "grommet";
import DataTable, {createTheme} from "react-data-table-component";
import styled, {StyleSheetManager} from "styled-components";
import {normalizeColor} from "grommet/utils";
import Section from "./Section";
import ExportButton from "./ExportButton";
import ExportDropButton from "./ExportDropButton";
import DataTableSearchInput from "./DataTableSearchInput";
import {debounce, useDebounce, useDebounceCallback} from "../utils/debounce";
import {useSession} from "next-auth/react";
import {csvFilename} from "../utils/csv";
import {forwardDataTableProp} from "../utils/dataTable";
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from "../constants/DataTable";
import {
    IconArrowDown
} from '@tabler/icons-react';
import Icon from "./Icon";
import Button from "./Button";
import { snakeCase } from "../utils/string";
import { trackClick } from "../utils/segment";
import AmountFilter from "./filters/AmountFilter";
import DateFilter from "./filters/DateFilter";


const createThemeForDataTable = (theme) => {
    const textColor = normalizeColor(theme.global.colors.text, theme);
    const hoverColor = normalizeColor(theme.global.colors.hover, theme);
    createTheme('grommet', {
        text: {
            primary: textColor,
            secondary: textColor
        },
        background: {
            default: 'transparent',
        },
        button: {
            default: textColor,
            focus: textColor,
            hover: hoverColor,
            disabled: textColor,
        },
        context: {
            background: 'transparent',
            text: textColor,
        },
        sortFocus: {
            default: textColor
        }
    });
};

const getCustomStyles = (theme, clickable, plain, showRowSeparators) => {
    const { global } = theme;
    const { colors } = global;
    const focusColor = normalizeColor(colors.focus, theme);
    const borderColor = normalizeColor(colors.border, theme);
    const activeColor = normalizeColor(colors.accent, theme);
    const textColor = normalizeColor(colors.text, theme);
    const activeTextColor = normalizeColor(global.hover.color, theme);
    const hoverColor = normalizeColor(colors.hover, theme);
    const disabledColor = normalizeColor(colors['text-weak'], theme);
    const bgColor = normalizeColor(colors.background, theme);
    return {
        body: {
            style: {
                padding: '3px'
            }
        },
        headCells: {
            style: {
                fontFamily: theme.global.font.family,
                fontSize: theme.text.xsmall.size,
                textTransform: 'uppercase',
                fontWeight: '600',
                wordSpacing: '2px',
                padding: '12px 6px'
            },
            inactiveSortStyle: {
                '&:focus': {
                    boxShadow: `0 0 2px 2px ${focusColor}`,
                    borderRadius: theme.global.control.border.radius,
                    height: '30px'
                },
            },
            activeSortStyle: {
                '&:focus': {
                    boxShadow: `0 0 2px 2px ${focusColor}`,
                    borderRadius: theme.global.control.border.radius,
                    height: '30px'
                },
            }
        },
        headRow: {
            style: { 
                ...(plain ? {} : { borderBottomColor: borderColor }),
                backgroundColor: bgColor
            },
        },
        header: {
            style: {
                minHeight: '0'
            }
        },
        rows: {
            style: {
                borderBottom: `${showRowSeparators ? `1px solid ${borderColor}` : 'none'} !important`,
                borderRadius: theme.global.control.border.radius,
                '&:hover': {
                    backgroundColor: clickable ? `${hoverColor}!important` : 'none',
                    color: clickable ? `${activeTextColor}!important` : textColor
                },
                '&:focus': {
                    boxShadow: `0 0 2px 2px ${focusColor}`,
                    outline: 'none !important',
                    position: 'relative',
                    border: 'none',
                    backgroundColor: clickable ? activeColor : 'none',
                    color: clickable ? activeTextColor : textColor
                }
            },
            selectedHighlightStyle: {
                // use nth-of-type(n) to override other nth selectors
                '&:nth-of-type(n)': {
                    backgroundColor: hoverColor,
                    color: textColor
                },
            },
        },
        cells: {
            style: {
                fontSize: theme.text.medium.size,
                lineHeight: '24px',
                padding: '12px 6px'
            },
        },
        pagination: {
            pageButtonsStyle: {
                borderRadius: '5px',
                '&:focus': {
                    boxShadow: `0 0 2px 2px ${focusColor}`,
                    outline: 'none',
                    border: 'none',
                    backgroundColor: 'none'
                },
                '&:disabled': {
                    fill: disabledColor,
                },
                '&:hover:not(:disabled)': {
                    backgroundColor: activeColor
                },
            }
        }
    }
};

// Palette of bar widths; a per-cell index into this makes skeleton rows read as
// varied content rather than a uniform block.
const SKELETON_WIDTHS = ['40%', '55%', '65%', '48%', '72%', '58%', '80%', '45%', '62%', '52%', '35%', '68%'];
// Random row count per load (within this range) so the skeleton looks organic
// rather than mechanically identical every time. Kept >= 4 so it doesn't read as
// an empty/broken table, and capped so the height doesn't jump too much.
const SKELETON_MIN_ROWS = 4;
const SKELETON_MAX_ROWS = 8;
// Matches the content height of a populated row, so swapping one for the other moves nothing.
const SKELETON_ROW_CONTENT_HEIGHT = '32px';

// react-data-table provides no customization for the row dropdown in pagination
const DataTableStyledContainer = styled(Box)`
  overflow: hidden;
  .rdt_Pagination select:focus {
    outline: none;
    box-shadow: 0 0 2px 2px ${props => normalizeColor(props.theme.global.colors.focus, props.theme)};
    border-radius: ${props => props.theme.global.control.border.radius}
  }
  .rdt_TableBody {
      position: relative;
      padding: 5px 0;
  }
  label[class^="StyledCheckBox__StyledCheckBoxContainer"]:hover {
    background: none;
  }
  label[class^="StyledCheckBox__StyledCheckBoxContainer"]:focus > div[class^="StyledCheckBox"],
  .rdt_TableCell > input[type="checkbox"]:focus, .rdt_TableCol > input[type="checkbox"]:focus {
    outline: none;
    box-shadow: 0 0 2px 2px ${props => normalizeColor(props.theme.global.colors.focus, props.theme)};
  }
  .rdt_TableRow button {
    &:hover {
        background-color: ${props => normalizeColor(props.theme.global.colors.hover, props.theme)};
    }
    &:focus {
        box-shadow: 0 0 2px 2px ${props => normalizeColor(props.theme.global.colors.focus, props.theme)};
        background-color: transparent;
    }
  }
  .rdt_Table {
      // No side padding, so rows line up with the section title above the table
      padding: 3px 0;
  }
  /*
    On a phone the library's centred 48px checkbox cell inset the checkbox 12px from the
    edge of the table. Left-aligned in 36px, it keeps 12px before the content. The
    selection cell is always the first one in a row.
  */
  ${props => props.isMobile ? `
  .rdt_TableRow > div:first-child:has(input[type="checkbox"]),
  .rdt_TableHeadRow > div:first-child:has(input[type="checkbox"]) {
      flex: 0 0 36px;
      min-width: 36px;
      justify-content: flex-start;
  }` : ''}
  .rdt_TableCell {
    ${props => props.isMobile && props.isSingleColumn ? 'padding: 10px 0 !important;' : ''}
   }
`;

/**
 *
 * @param props: {
 *      theme: object - grommet theme object
 *      prefix: string - prefix attach to element IDs
 *      data: object - data to render in data table
 * }
 */
const DataTableComponent = (props) => {
    const {
        data,
        prefix,
        title,
        collapsible,
        columns,
        emptyListMessage,
        dataTableWrapperProps,
        onRowClicked,
        resetSelectedRowsRef,
        controlWrapperProps = {},
        rightControlsWrapperProps = {},
        leftControlsWrapperProps = {},
        // filtering
        onSearch,
        onDateRangeSet,
        dateRange,
        onAmountRangeSet,
        amountRange,
        controls, // on left side
        rightControls, // next to search bar
        // pagination
        resetPaginationToggle,
        onOffsetChange,
        defaultRowPerPage = DEFAULT_ROWS_PER_PAGE,
        numRows,
        hidePagination = false,
        pageSizeOptions,
        //sorting
        onSort,
        //selectable
        selectableRows,
        rowName,
        selectedControls,
        onSelectedRowsChange = () => {},
        persistSelections, // persist row selection when page / sorting changes. "select all" will be disabled if true
        preselectCriteria,
        //expandable
        expandableRows,
        expandableRowsComponent,
        expandableRowDisabled,
        // CSV stuff
        dataName,
        tab,
        hideExport = false,
        exportConfigs,
        plain = false,
        isServer = true,
        keyField = null,
        icon,
        isDataFiltered = false, // to see if export csv name need to show "filtered"
        getFullData, // a function used to get full data for csv export, if BE search is enabled
        onExportCSVClicked,
        // PDF export
        onExportPDFClicked,
        showPDFExport = false,
        defaultSort,
        defaultSearchText,
        limit,
        //UI
        showRowSeparators = false,
        isLoading = false,
        hideControls = false,
        ...rest
    } = props;

    const [searchText, setSearchText] = React.useState(defaultSearchText);
    const [selectedRows, setSelectedRows] = useState([]);
    const {data: session} = useSession();
    const [rowsPerPage, setRowsPerPage] = useState(limit || defaultRowPerPage);
    const [selectAllByPage, setSelectAllByPage] = useState(true);
    const [isLastPage, setIsLastPage] = useState(data.length <= rowsPerPage);
    const [rowClickedData, setRowClickedData] = useState();
    const debouncedKeyword = useDebounce(searchText, 400);
    useEffect(() => {
        onSearch && onSearch(debouncedKeyword)
    }, [debouncedKeyword]);
    const handleOnRowClicked = useDebounceCallback(() => onRowClicked && onRowClicked(rowClickedData), 10);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const isSingleColumn = columns && columns.length === 1;
    const [isSelectedRowsInitiated, setIsSelectedRowsInitiated] = useState(false);

    /*
        While loading, render placeholder skeleton rows instead of a spinner so the column
        header and layout paint immediately.

        Deterministic rather than random, because a count that changed every mount could not
        match what replaced it. Still capped: this is only ever a guess at how many rows are
        coming, and painting a hundred placeholders for a list that turns out to hold three
        is a worse jump than a short block that grows. Returning to a list does not reach
        here at all now - cached rows render straight away - so this is the genuinely
        unknown first load.
    */
    const skeletonRowCount = React.useMemo(
        () => Math.max(SKELETON_MIN_ROWS, Math.min(rowsPerPage || SKELETON_MAX_ROWS, SKELETON_MAX_ROWS)),
        [rowsPerPage]
    );
    const skeletonData = React.useMemo(
        () => Array.from({length: skeletonRowCount}, (_, i) => ({id: `__skeleton_${i}`, __skeleton: true})),
        [skeletonRowCount]
    );
    // react-data-table-component v7 matches `defaultSortFieldId` against a column's
    // `id`, and falls back to positional ids (1, 2, 3...) for columns that don't
    // declare one. Our columns are keyed by `sortField`, which is also what we store
    // in the sort preference, so mirror it into `id`. Without this the remembered
    // sort column is never marked selected: no sort arrow, and the first click on it
    // re-sends the direction it already has instead of toggling.
    const identifiedColumns = React.useMemo(() => {
        const usedIds = new Set();
        return columns.map((col) => {
            if (col.id !== undefined || !col.sortField || usedIds.has(col.sortField)) return col;
            usedIds.add(col.sortField);
            return {...col, id: col.sortField};
        });
    }, [columns]);

    const skeletonColumns = React.useMemo(
        () => identifiedColumns.map((col, colIndex) => ({
            ...col,
            sortable: false,
            // Deterministic per-cell width (row x column), nudged by the mount's random
            // row count so the pattern also varies load-to-load. Coprime multipliers
            // spread the picks so adjacent cells rarely match.
            /*
                The bar sits in a box the height a real row's content occupies, so a
                skeleton row is the same height as the row that replaces it. Without it the
                placeholder rows were 36px against the real 56px, and the table grew by
                20px per row as the data landed - the jump you see coming back to a list.
            */
            cell: (row, rowIndex) => (
                <Box justify="center" height={SKELETON_ROW_CONTENT_HEIGHT} fill="horizontal">
                    <Skeleton
                        height="12px"
                        round="small"
                        width={SKELETON_WIDTHS[(rowIndex * 3 + colIndex * 7 + skeletonRowCount) % SKELETON_WIDTHS.length]}
                    />
                </Box>
            ),
        })),
        [identifiedColumns, skeletonRowCount]
    );
    const tableData = isLoading ? skeletonData : data;
    const tableColumns = isLoading ? skeletonColumns : identifiedColumns;

    const searchMemo = React.useMemo(
        () => (
            <DataTableSearchInput
                prefix={prefix}
                value={searchText}
                onChange={(e) => {
                    setSearchText(e.target.value);
                }}
            />
        ), [searchText],
    );

    const onSelectedRowsChangeHandler = (data) => {
        const {selectedRows: nextSelectedRows} = data;
        setSelectedRows(nextSelectedRows);
        onSelectedRowsChange(data)
    }

    const clearSelectedRows = () => {
        setSelectedRows([]);
        setSelectAllByPage(false);
    }

    useEffect(() => {
        resetSelectedRowsRef && (resetSelectedRowsRef.current = clearSelectedRows);
    }, []);

    /*
        react-data-table only fires onRowClicked when the clicked element *itself* carries
        data-tag="allowRowEvents". It puts that tag on the cell, not on whatever the cell
        renders, so any column whose cell returns its own element is a dead patch in an
        otherwise clickable row: an invoice or customer tag, a status badge, a wrapped bit
        of text. Columns returning a bare string work only by accident, because the text
        node's parent is the tagged cell.

        This closes the gap without tagging every leaf by hand. When a click lands inside a
        tagged cell but on one of its children, we re-dispatch on the cell so the library
        resolves the row itself. Cells marked ignoreRowClick carry no tag, so row actions
        stay excluded, and anything genuinely interactive is skipped so links, buttons and
        checkboxes keep their own behaviour.
    */
    const rowClickAreaRef = useRef(null);
    useEffect(() => {
        const container = rowClickAreaRef.current;
        if (!container || !onRowClicked) return;

        const onClick = (event) => {
            const target = event.target;
            if (!target || typeof target.closest !== 'function') return;
            // Already tagged: react-data-table is about to handle this one itself.
            if (target.getAttribute && target.getAttribute('data-tag') === 'allowRowEvents') return;
            if (target.closest('a, button, input, select, textarea, label, [role="button"], [role="checkbox"]')) return;
            /*
                This listener is native and fires before React's, so a child calling
                stopPropagation in an onClick cannot stop it. Cells that render their own
                non-native control opt out with data-ignore-row-click instead.
            */
            if (target.closest('[data-ignore-row-click]')) return;
            const cell = target.closest('[data-tag="allowRowEvents"]');
            if (!cell || !container.contains(cell)) return;
            cell.click();
        };

        container.addEventListener('click', onClick);
        return () => container.removeEventListener('click', onClick);
    }, [onRowClicked]);

    // To "force" react-data-table to support keyboard focus
    useEffect(() => {
        if (!onRowClicked) return;
        if (typeof window !== "undefined") {
            const els = document.getElementsByClassName('rdt_TableRow');
            const isCheckbox = (el) => el?.type === "checkbox" || el?.getAttribute('class')?.includes('StyledCheckBox__');
            for (let i = 0; i < els.length; i++) {
                const el = els[i];
                if (el.tabIndex !== 0) {
                    el.tabIndex = 0;
                    el.addEventListener('keypress', e => {
                        if (isCheckbox(e.target)) return; // if check box is selected, ignore
                        if (e.key === "Enter" || e.keyCode === "Space") {
                            el.lastElementChild?.click();
                        }
                    });
                }
            }
        }
    });

    useEffect(() => {
        if (selectAllByPage) return;
        if (typeof window !== "undefined") {
            const input = document.getElementsByName('select-all-rows').item(0);
            input?.click();
            input?.click(); //intentionally click twice to make sure it's unchecked
            setSelectAllByPage(true);
        }
    }, [selectAllByPage]);

    const showOptionToSelectAllPages = () => {
        if (!onSelectedRowsChange) return false;
        if (data.length <= rowsPerPage) return false; // there is only one page in the data table
        // selected all rows on current page, or selected all rows on last page
        return ((selectedRows.length === rowsPerPage) || (isLastPage && (selectedRows.length === (data.length % rowsPerPage))));
    }

    const onSortHandler = (sortColumn, direction) => {
        onSort(sortColumn?.sortField, direction);
    }
    const debouncedOnSort = useCallback(debounce(onSortHandler, 300), []);

    return (
        <ThemeContext.Consumer>
            {(theme) => {
                createThemeForDataTable(theme);
                return (
                    <Section
                        title={title}
                        icon={icon}
                        collapsible={collapsible}
                        control={hideControls && selectedRows.length === 0 ? (<></>) : (
                            <Box direction="row" gap="small" align="center" justify="between" fill flex={false} wrap {...controlWrapperProps}>
                                {selectedRows.length === 0 || !selectedControls ?
                                    (
                                        <Box direction="row-responsive" fill justify="between">
                                            <Box gap="small" direction="row" align={isMobile ? "start" : "center"} wrap {...leftControlsWrapperProps}>
                                                {React.Children.map(controls, (child) => (
                                                    <Box margin={{vertical: isMobile ? "xsmall" : "none"}}>{child}</Box>
                                                ))}
                                                {onDateRangeSet && (
                                                    <Box margin={{vertical: isMobile ? "xsmall" : "none"}}>
                                                        <DateFilter
                                                            selectedRange={dateRange}
                                                            onApplyFilter={(e) => {
                                                                onDateRangeSet(e ? [e.start, e.end] : undefined);
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                {onAmountRangeSet && (
                                                    <Box margin={{vertical: isMobile ? "xsmall" : "none"}}>
                                                        <AmountFilter
                                                            selectedAmountRange={amountRange}
                                                            onApplyFilter={(e) => {
                                                                onAmountRangeSet(e ? [e.min, e.max] : undefined);
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box gap="small" direction="row-responsive" align={isMobile ? "end" : "center"} pad={{right: 'xxsmall'}} {...rightControlsWrapperProps}>
                                                {rightControls}
                                                {onSearch && searchMemo}
                                                {!hideExport && !isMobile && ((data?.length || 0) > 0) && (
                                                    showPDFExport ? (
                                                        <ExportDropButton
                                                            {...(onExportCSVClicked ? {
                                                                onExportCSVClicked,
                                                                dataName
                                                            } : {
                                                                data,
                                                                exportConfigs,
                                                                filename: csvFilename(dataName || title, { businessName: session?.company?.business_name || session?.company?.legal_name }),
                                                                isFullData: !isServer,
                                                                getFullData,
                                                                isDataFiltered,
                                                                dataName
                                                            })}
                                                            onExportPDFClicked={onExportPDFClicked}
                                                            showPDFOption={showPDFExport}
                                                        />
                                                    ) : (
                                                        <ExportButton
                                                            {...(onExportCSVClicked ? {
                                                                onExportCSVClicked,
                                                                dataName
                                                            } : {
                                                                data,
                                                                exportConfigs,
                                                                filename: csvFilename(dataName || title, { businessName: session?.company?.business_name || session?.company?.legal_name }),
                                                                isFullData: !isServer,
                                                                getFullData,
                                                                isDataFiltered,
                                                                dataName
                                                            })}
                                                        />
                                                    ))}
                                            </Box>
                                        </Box>
                                    ) :
                                    (
                                        // On a phone the count and the bulk actions shared one row, so each
                                        // broke onto two lines. The actions go under the count instead, at
                                        // their own width: stretched, a bordered menu became a full-width bar.
                                        <Box direction={isMobile ? "column" : "row"} gap="small" pad="xsmall" align={isMobile ? "start" : "center"} justify="between" fill="horizontal">
                                            <Box direction="row" gap="small" pad="xsmall" align="center" wrap={isMobile}>
                                                <Box>
                                                    <Text weight={500} size="medium">
                                                        {showOptionToSelectAllPages() ?
                                                            (`${selectedRows.length} ${rowName ? rowName?.plural : 'items'} on this page are selected`) :
                                                            (`${selectedRows.length} ${selectedRows.length > 1 ? ((rowName && rowName?.plural) || 'items') : ((rowName && rowName?.single) || 'item')} selected`)}
                                                    </Text>
                                                </Box>
                                                {showOptionToSelectAllPages() && (
                                                    <Box pad={{horizontal: 'small'}}>
                                                        <Button
                                                            event={{
                                                                section: `${snakeCase(dataName)}_data_table`,
                                                                element: 'select_all_pages'
                                                            }}
                                                            onClick={() => setSelectAllByPage(false)}
                                                            hoverIndicator
                                                            secondary
                                                            label={(<Text fontWeight={400}>Select all pages instead</Text>)}/>
                                                    </Box>
                                                )}
                                            </Box>
                                            {selectedControls}
                                        </Box>
                                    )}
                            </Box>
                        )}
                        titleProps={{
                            margin: {
                                vertical: 'small',
                                horizontal: 'xsmall'
                            }
                        }}
                        fullWidthControls
                        pad={{vertical: 'xsmall'}}
                        headerProps={{margin: "none", pad: {bottom: "xsmall"}}}
                        contentProps={{pad: 'none'}}
                        {...dataTableWrapperProps}>
                        <DataTableStyledContainer ref={rowClickAreaRef} theme={theme} isMobile={isMobile} isSingleColumn={isSingleColumn}>
                            <StyleSheetManager shouldForwardProp={forwardDataTableProp}>
                                <DataTable
                                    dense={true}
                                    // Loading is shown as skeleton rows (tableData/tableColumns below),
                                    // not react-data-table's built-in spinner. progressPending stays
                                    // false so the body (skeleton rows) renders and the header shows.
                                    progressPending={false}
                                    data={tableData}
                                    columns={tableColumns}
                                    noDataComponent={
                                        <Box pad="medium">
                                            <Text color="text-weak">{emptyListMessage}</Text>
                                        </Box>
                                    }
                                    // styling
                                    customStyles={getCustomStyles(theme, !!onRowClicked, plain, showRowSeparators)}
                                    fontSize={theme.global.font.size}
                                    theme="grommet"
                                    // pagination
                                    onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                                        const paginationRowPerPageOptions = pageSizeOptions || PAGE_SIZE_OPTIONS
                                        // Guard against currentRowsPerPage === rowsPerPage: react-data-table's
                                        // "on rows-per-page change" hook is a did-update effect that React
                                        // StrictMode double-invokes on mount, firing this with no real change.
                                        // Without this guard that spurious event resets offset to 0, wiping a
                                        // restored page position.
                                        if (currentRowsPerPage >= paginationRowPerPageOptions[0] && currentRowsPerPage !== rowsPerPage) {
                                        // If the number of rows per page is changed,
                                        // reset offset to 0 and
                                        // update the number of rows per page
                                            onOffsetChange && onOffsetChange(0, currentRowsPerPage);
                                            setRowsPerPage(currentRowsPerPage);
                                        }
                                    }}
                                    onChangePage={(page, totalRows) => {
                                        const newOffset = (page - 1) * rowsPerPage
                                        const isLastPageBoolean = page === Math.ceil(totalRows / rowsPerPage)
                                        onOffsetChange && onOffsetChange(newOffset, rowsPerPage);
                                        setIsLastPage(isLastPageBoolean);
                                    }}
                                    {...(persistSelections ? {
                                        paginationServerOptions: {
                                            persistSelectedOnPageChange: true,
                                            persistSelectedOnSort: true
                                        }
                                    } : {})}
                                    pagination={!isLoading && !hidePagination && (numRows > defaultRowPerPage)}
                                    // paginationPerPage must be passed unconditionally: react-data-table
                                    // seeds its internal rowsPerPage from it at mount and never re-reads
                                    // it. If it's absent at mount (e.g. while loading, pagination off) RDT
                                    // defaults to 10, then computes page numbers against 10 while our
                                    // onChangePage multiplies by the real page size — producing bogus
                                    // offsets (e.g. "last page" -> offset 1300 for 269 rows).
                                    paginationPerPage={limit || defaultRowPerPage}
                                    {...(!isLoading && numRows > defaultRowPerPage ? {
                                        paginationRowsPerPageOptions: pageSizeOptions || PAGE_SIZE_OPTIONS,
                                        paginationServer: isServer,
                                        paginationTotalRows: numRows,
                                        paginationResetDefaultPage: resetPaginationToggle
                                    } : {})}
                                    sortIcon={<Icon SelectedIcon={IconArrowDown} size="xsmall"/>}
                                    sortServer={isServer}
                                    {...(defaultSort ? {
                                        defaultSortFieldId: defaultSort.col,
                                        defaultSortAsc: defaultSort.direction === 'asc'
                                    } : {})}
                                    {...(onSort && !isLoading ? {onSort: debouncedOnSort} : {})}
                                    // row click
                                    highlightOnHover={!isLoading && !!onRowClicked}
                                    pointerOnHover={!isLoading && !!onRowClicked}
                                    onRowClicked={(data) => {
                                        if (isLoading) return;
                                        trackClick({
                                            section: `${dataName || ''}_data_table`,
                                            element: 'row',
                                            description: data?.id
                                        })
                                        setRowClickedData(data);
                                        handleOnRowClicked();
                                    }}
                                    // selectable rows
                                    selectableRows={!isLoading && selectableRows}
                                    selectableRowsComponent={forwardRef((props, ref) => (
                                        <CheckBox {...props} />
                                    ))}
                                    selectableRowsHighlight={true}
                                    /* Work around for re-render on preselect
                                    Explanation:
                                        The react-data-table DataTable component accepts a function to preselect rows based on a criteria.
                                        However, since we use the DataTable in DataTableWithBackendSearch, the table gets re-rendered often.
                                        We need to be able to turn off this preselected criteria on initialize or else selected rows will be
                                        overwritten on rerenders.
                                    */
                                    {...(preselectCriteria ? {selectableRowSelected: isSelectedRowsInitiated ? undefined : preselectCriteria} : {})}
                                    onSelectedRowsChange={(data) => {
                                        if (preselectCriteria && !isSelectedRowsInitiated) {
                                            setIsSelectedRowsInitiated(true);
                                            return;
                                        }
                                        onSelectedRowsChangeHandler(data);
                                    }}
                                    selectableRowsVisibleOnly={persistSelections ? false : selectAllByPage}
                                    selectableRowsComponentProps={{
                                        a11yTitle: `Select entry`,
                                        indeterminate: isIndeterminate => isIndeterminate
                                    }}
                                    // expandable rows
                                    {...(expandableRows ? {
                                        expandableRows: true,
                                        expandableRowsComponent,
                                        expandableRowDisabled: expandableRowDisabled || (() => false)
                                    } : {})}
                                    {...(keyField ? {keyField} : {})}
                                    {...rest}
                                />
                            </StyleSheetManager>
                        </DataTableStyledContainer>
                    </Section>
                );
            }}
        </ThemeContext.Consumer>
    );
}

export default React.memo(DataTableComponent);
